import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AuthLayout } from '../components/AuthLayout';
import { GoogleButton } from '../components/GoogleButton';
import { getAuthErrorMessage } from '../lib/authErrors';

interface LocationState {
    from?: { pathname?: string };
}

export function LoginPage() {
    const { signInWithEmail, signInWithGoogle } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = (location.state as LocationState)?.from?.pathname ?? '/dashboard';

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const handleEmailLogin = async (e: FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);
        try {
            await signInWithEmail(email.trim(), password);
            navigate(from, { replace: true });
        } catch (err) {
            setError(getAuthErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const handleGoogle = async () => {
        setError(null);
        setLoading(true);
        try {
            // Redirection OAuth : le navigateur part chez Google puis revient
            // sur /app/dashboard — pas besoin de navigate() manuel.
            await signInWithGoogle();
        } catch (err) {
            setError(getAuthErrorMessage(err));
            setLoading(false);
        }
    };

    return (
        <AuthLayout
            title="Connexion"
            subtitle="Accède à ton espace Quotio pour gérer tes générateurs."
            footer={
                <>
                    Pas encore de compte ? <Link to="/signup">Créer un compte</Link>
                </>
            }
        >
            <GoogleButton onClick={handleGoogle} disabled={loading} />

            <div className="auth-divider"><span>ou</span></div>

            <form onSubmit={handleEmailLogin} className="auth-form">
                <label className="field">
                    <span className="field-label">Email</span>
                    <input
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="toi@exemple.com"
                    />
                </label>

                <label className="field">
                    <span className="field-label">Mot de passe</span>
                    <input
                        type="password"
                        autoComplete="current-password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                    />
                </label>

                {error && <div className="auth-error" role="alert">{error}</div>}

                <button type="submit" className="btn-primary" disabled={loading}>
                    {loading ? 'Connexion…' : 'Se connecter'}
                </button>
            </form>
        </AuthLayout>
    );
}
